<template>
  <div>
    <div class="d-flex align-items-end flex-column gap-2 flex-wrap">
      <!-- Main date range dropdown -->
      <div class="dropdown">
        <button
            class="btn btn-sm btn-outline-secondary dropdown-toggle"
            type="button"
            data-bs-toggle="dropdown"
            aria-expanded="false">
          <span class="text-truncate" v-html="currentRangeLabel"></span>
        </button>
        <ul class="dropdown-menu dropdown-menu-end">
          <li v-for="opt in rangeOptions" :key="opt.key">
            <a
                class="dropdown-item"
                :class="{'active': props.modelValue.preset === opt.key}"
                href="#"
                @click.prevent="handlePresetChange(opt.key)"
            >
              {{ opt.label }}
            </a>
          </li>
        </ul>
      </div>

      <div class="d-flex align-items-center gap-1 justify-content-center">
        <!-- Comparison toggle -->
        <span class="fw-bold" style="font-size:0.7rem;line-height:0.7;">vs:</span>
        <div class="d-flex align-items-center justify-content-center text-muted" style="font-size:0.7rem;line-height:0.7;">
          <button
              :class="['btn btn-link btn-sm p-0', currentComparison === 'prev' ? 'text-body fw-semibold' : 'text-muted']"
              style="font-size:inherit;line-height:inherit;"
              @click="setComparison('prev')"
          >previous period</button>
          <span class="mx-1">|</span>
          <button
              :class="['btn btn-link btn-sm p-0', currentComparison === 'yoy' ? 'text-body fw-semibold' : 'text-muted', !yoyAvailable ? 'disabled' : '']"
              style="font-size:inherit;line-height:inherit;"
              :title="!yoyAvailable ? 'Date range exceeds one year' : ''"
              @click="yoyAvailable && setComparison('yoy')"
          >year over year</button>
        </div>
      </div>

    </div>

    <!-- Custom Range Inline Form -->
    <transition name="slide-fade">
      <div v-if="showCustomForm" class="custom-range-form mt-2 p-3 border rounded bg-light">
        <div class="row g-2 mb-2">
          <div class="col-md-6">
            <label class="form-label small mb-1">From Date</label>
            <input type="date" v-model="customFrom" class="form-control form-control-sm" />
          </div>
          <div class="col-md-6">
            <label class="form-label small mb-1">To Date</label>
            <input type="date" v-model="customTo" class="form-control form-control-sm" />
          </div>
        </div>
        <div class="row g-2 mb-2">
          <div class="col-md-12">
            <label class="form-label small mb-1">Interval</label>
            <select v-model="customInterval" class="form-select form-select-sm">
              <option value="auto">Auto ({{ autoInterval }})</option>
              <option value="day">Day</option>
              <option value="week">Week</option>
              <option value="month">Month</option>
              <option value="quarter">Quarter</option>
            </select>
          </div>
        </div>
        <div class="d-flex gap-2">
          <button @click="applyCustomRange" class="btn btn-primary btn-sm">Apply</button>
          <button @click="cancelCustomRange" class="btn btn-secondary btn-sm">Cancel</button>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup>
import { defineProps, defineEmits, computed, ref, watch } from 'vue' // ref still used by showCustomForm/customFrom/customTo/customInterval/previousPreset
import { useStore } from 'vuex'
import { DateUtil } from "@/services/dateUtils"

const props = defineProps({
  modelValue: {
    type: Object,
    default: () => ({ from: new Date(), to: new Date(), interval: 'day', preset: DateUtil.RANGES.LAST_7_DAYS, compare:true })
  }
})

const emit = defineEmits(['update:modelValue'])
const store = useStore()

// Get timezone from store config, fallback to app default timezone (matches database default)
const storeConfig = computed(() => store.state.eshop.eshop)
const customWeekStartDay = computed(() => storeConfig.value?.config?.WEEK_START_DAY)
const dateUtil = computed(() => {
  const timezone = storeConfig.value?.timezone || store.state.defaultTimezone
  const weekStartDay = customWeekStartDay.value !== undefined ? parseInt(customWeekStartDay.value) : 1
  return new DateUtil(timezone, weekStartDay)
})

// ----- Comparison State -----
const rangeDays = computed(() => {
  const from = props.modelValue?.from
  const to = props.modelValue?.to
  if (!from || !to) return 0
  if (from.diff) return Math.round(to.diff(from, 'days').days)
  return Math.round((new Date(to) - new Date(from)) / 86400000)
})

const yoyAvailable = computed(() => rangeDays.value <= 366)
const currentComparison = computed(() => props.modelValue?.comparison || 'prev')

// If range grows beyond a year while YoY is active, fall back to prev
watch(rangeDays, (days) => {
  if (days > 366 && props.modelValue?.comparison === 'yoy') {
    setComparison('prev')
  }
})

function setComparison(key) {
  const r = { ...props.modelValue, comparison: key }
  store.commit('eshop/setLastDateRange', r)
  emit('update:modelValue', r)
}

// ----- Custom Range State -----
const showCustomForm = ref(false)
const customFrom = ref('')
const customTo = ref('')
const customInterval = ref('auto')
const previousPreset = ref(DateUtil.RANGES.LAST_7_DAYS)

// Auto-detect interval based on selected date range
const autoInterval = computed(() => {
  if (!customFrom.value || !customTo.value) return 'day'

  const from = dateUtil.value.fromISO(customFrom.value)
  const to = dateUtil.value.fromISO(customTo.value)
  return dateUtil.value.autoDetectInterval(from, to)
})

// Helper to get day name
const getDayName = (dayNum) => {
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
  return days[dayNum] || 'Mon'
}

// Dynamic options - update custom range label when active
const rangeOptions = computed(() => {
  let options = DateUtil.getRangeOptions()

  // Update standard week labels to show start day (Monday)
  options = options.map(opt => {
    if (opt.key === DateUtil.RANGES.THIS_WEEK) {
      return { ...opt, label: 'This Week (starts Mon)' }
    }
    if (opt.key === DateUtil.RANGES.LAST_WEEK) {
      return { ...opt, label: 'Last Week (starts Mon)' }
    }
    return opt
  })

  // Add custom week presets if the week start day is not the default (1 = Monday)
  const DEFAULT_WEEK_START_DAY = 1
  const hasCustomWeekStart = customWeekStartDay.value !== undefined && parseInt(customWeekStartDay.value) !== DEFAULT_WEEK_START_DAY

  if (hasCustomWeekStart) {
    const dayName = getDayName(customWeekStartDay.value)
    const customWeekOptions = [
      { group: 'Calendar Views', key: DateUtil.RANGES.THIS_WEEK_CUSTOM, label: `This Week (starts ${dayName})` },
      { group: 'Calendar Views', key: DateUtil.RANGES.LAST_WEEK_CUSTOM, label: `Last Week (starts ${dayName})` }
    ]

    // Insert custom week options after the standard week options
    const lastWeekIndex = options.findIndex(opt => opt.key === DateUtil.RANGES.LAST_WEEK)
    if (lastWeekIndex !== -1) {
      options = [
        ...options.slice(0, lastWeekIndex + 1),
        ...customWeekOptions,
        ...options.slice(lastWeekIndex + 1)
      ]
    }
  }

  return options
})

// Format date for display (M/D/YY format)
const formatDateShort = (dt) => {
  if (!dt) return ''
  const luxonDate = dt.toJSDate ? dt : dateUtil.value.fromISO(dt)
  const month = luxonDate.month
  const day = luxonDate.day
  const year = String(luxonDate.year).slice(-2)
  return `${month}/${day}/${year}`
}

// Current range label with dates
const currentRangeLabel = computed(() => {
  const currentOption = rangeOptions.value.find(opt => opt.key === props.modelValue.preset)
  let label = currentOption?.label || 'Select Range'

  if (props.modelValue.from && props.modelValue.to) {
    // Remove ellipsis when displaying with dates
    label = label.replace('...', '')
    const fromDate = formatDateShort(props.modelValue.from)
    const toDate = formatDateShort(props.modelValue.to)
    return `${label}: <span class="small fw-bold"><small>${fromDate}-${toDate}</small></span>`
  }

  return label
})

// ----- Preset handler -----
function handlePresetChange(key) {
  if (key === DateUtil.RANGES.CUSTOM_RANGE) {
    showCustomForm.value = true
    // Initialize with current range values (works for both custom and preset ranges)
    if (props.modelValue.from && props.modelValue.to) {
      const fromDate = props.modelValue.from.toJSDate ? props.modelValue.from : dateUtil.value.fromISO(props.modelValue.from)
      const toDate = props.modelValue.to.toJSDate ? props.modelValue.to : dateUtil.value.fromISO(props.modelValue.to)
      customFrom.value = dateUtil.value.toISODate(fromDate)
      customTo.value = dateUtil.value.toISODate(toDate)
      customInterval.value = props.modelValue.interval || 'auto'
    } else {
      const now = dateUtil.value.now()
      customFrom.value = dateUtil.value.toISODate(now.minus({ days: 7 }))
      customTo.value = dateUtil.value.toISODate(now)
      customInterval.value = 'auto'
    }
  } else {
    previousPreset.value = key
    showCustomForm.value = false
    applyPreset(key)
  }
}

function applyPreset(key) {
  const r = dateUtil.value.range(key)
  r.comparison = props.modelValue?.comparison || 'prev'
  store.commit('eshop/setLastDateRange', r)
  emit('update:modelValue', r)
}

function applyCustomRange() {
  if (!customFrom.value || !customTo.value) {
    alert('Please select both from and to dates')
    return
  }

  const from = dateUtil.value.fromISO(customFrom.value).startOf('day')
  const to = dateUtil.value.fromISO(customTo.value).endOf('day')

  if (from > to) {
    alert('From date must be before to date')
    return
  }

  const interval = customInterval.value === 'auto' ? autoInterval.value : customInterval.value

  const range = {
    from,
    to,
    interval,
    preset: DateUtil.RANGES.CUSTOM_RANGE,
    comparison: props.modelValue?.comparison || 'prev'
  }

  store.commit('eshop/setLastDateRange', range)
  emit('update:modelValue', range)

  showCustomForm.value = false
}

function cancelCustomRange() {
  showCustomForm.value = false
  // Revert to previous preset
  applyPreset(previousPreset.value)
}
</script>

<style scoped>
.custom-range-form {
  animation: slideDown 0.2s ease-out;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.slide-fade-enter-active {
  transition: all 0.2s ease-out;
}

.slide-fade-leave-active {
  transition: all 0.15s ease-in;
}

.slide-fade-enter-from {
  transform: translateY(-10px);
  opacity: 0;
}

.slide-fade-leave-to {
  transform: translateY(-10px);
  opacity: 0;
}
</style>
